<template>
  <div class="org-chart-wrapper">
    <div class="controls">
      <button 
        v-if="!dragEnabled" 
        @click="enableDrag" 
        class="action-btn"
        :disabled="loading"
      >
        Organize
      </button>
      
      <div v-else class="drag-actions">
        <button class="action-btn primary" @click="disableDrag">Done</button>
        <button 
          class="action-btn" 
          :disabled="undoActions.length === 0" 
          @click="undo"
        >
          Undo
        </button>
        <button 
          class="action-btn" 
          :disabled="redoActions.length === 0" 
          @click="redo"
        >
          Redo
        </button>
        <button class="action-btn warning" @click="cancelDrag">Cancel</button>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="loading-message">Cargando usuarios...</div>
    </div>
    <div ref="chartContainer" class="chart-container"></div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { OrgChart } from 'd3-org-chart';

export default {
  name: 'OrgChart',
  data() {
    return {
      dragEnabled: false,
      undoActions: [],
      redoActions: [],
      users: [],
      originalUsers: [],
      loading: false,
    };
  },
  mounted() {
    this.chart = null;
    this.dragNode = null;
    this.dropNode = null;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.isDragStarting = false;
    this.loadUsers();
  },
  beforeDestroy() {
    this.chart = null;
  },
  methods: {
    loadUsers() {
      this.loading = true;
      this.$http
        .get('/api/user')
        .then(response => {
          let allUsers = response.data.map(user => ({
            ...user,
            managerId: user.managerId || user.parentId || null
          }));

          const idsReferencedAsManager = new Set(
            allUsers.map(u => u.managerId).filter(id => id != null)
          );
          this.originalUsers = allUsers.filter(
            u => u.managerId != null || idsReferencedAsManager.has(u.id) || u.isOrganizationRoot
          );
          
          this.users = this.normalizeUsersForOrgChart([...this.originalUsers]);
          console.log("🚀 ~ this.users:", this.users)
          
          this.users.forEach(this.$q.profilePicSrc);
          
          this.initChart();
          this.loading = false;
        })
        .catch(response => {
          window.getApp.error("No se pudieron cargar los usuarios.");
          console.error(response);
          this.loading = false;
        });
    },
    
    normalizeUsersForOrgChart(users) {
      const usersWithoutManager = users.filter(u => !u.managerId);
      
      if (usersWithoutManager.length === 0) {
        return this.createVirtualRoot(users);
      } else if (usersWithoutManager.length === 1) {
        return users;
      } else {
        return this.resolveMultipleRoots(users, usersWithoutManager);
      }
    },
    
    createVirtualRoot(users) {
      const virtualRoot = {
        id: 'virtual-root-org',
        fullName: 'Organización',
        roles: 'Root',
        managerId: null,
        active: true,
        type: 0,
        email: '',
        blueTag: 'org',
        jobTitle: '',
        badges: [],
        customerProjects: [],
        department: 0,
        experienceLevelName: '',
        profilePicSrc: null
      };
      
      const normalizedUsers = users.map(user => {
        if (!user.managerId) {
          return { ...user, managerId: virtualRoot.id };
        }
        return user;
      });
      
      return [virtualRoot, ...normalizedUsers];
    },
    
    resolveMultipleRoots(users, roots) {
      let mainRoot = roots[0];
      
      const superAdmin = roots.find(r => r.isSuperAdmin);
      if (superAdmin) {
        mainRoot = superAdmin;
      } else {
        const sortedByDate = roots.sort((a, b) => {
          const dateA = a.blueniversary ? new Date(a.blueniversary) : new Date(0);
          const dateB = b.blueniversary ? new Date(b.blueniversary) : new Date(0);
          return dateA - dateB;
        });
        mainRoot = sortedByDate[0];
      }
      
      const otherRoots = roots.filter(r => r.id !== mainRoot.id);
      
      return users.map(user => {
        if (otherRoots.some(r => r.id === user.id)) {
          return { ...user, managerId: mainRoot.id };
        }
        return user;
      });
    },
    initChart() {
      console.log('init chart')
      console.log("🚀 ~ this.$refs.chartContainer:", this.$refs.chartContainer.getBoundingClientRect().width)
      if (!this.$refs.chartContainer || !this.users || this.users.length === 0) {
        return;
      }

      const vm = this;

      this.chart = new OrgChart()
          .defs((d) => {
            return `
              <marker id="arrow-${d.from + "_" + d.to}" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                <path d="M 0 0 L 10 3.5 L 0 7" fill="#E27396" />
              </marker>
            `;
          })
          .nodeHeight((d) => 85 + 25)
          .nodeWidth((d) => 220 + 2)
          .childrenMargin((d) => 50)
          .compactMarginBetween((d) => 35)
          .compactMarginPair((d) => 30)
          .neighbourMargin((a, b) => 20)
          .container(this.$refs.chartContainer)
          .parentNodeId((d) => {
            console.log("🚀 ~ d:", d)
            if (d.data) {
              return d.data.managerId || null;
            }
            return d.managerId || null;
          })
          .data(this.users)
          .initialExpandLevel(10)
          // .svgWidth(this.$refs.chartContainer.getBoundingClientRect().width)
          .nodeContent((d) => this.generateContent(d))
          .nodeEnter(function (node) {
            d3.select(this).call(
              d3.drag()
                .filter(function () {
                  return vm.dragEnabled && this.classList.contains('draggable');
                })
                .on('start', function (event, d) {
                  vm.onDragStart(this, event, d);
                })
                .on('drag', function (event, d) {
                  vm.onDrag(this, event);
                })
                .on('end', function (event, d) {
                  vm.onDragEnd(this, event);
                })
            );
          })
          .nodeUpdate(function (d) {
            const el = d3.select(this);
            if (d.id === 'virtual-root-org') {
              el.classed('droppable', false);
              el.classed('draggable', false);
            } else {
              el.classed('droppable', true);
              el.classed('draggable', true);
            }
          })
          .render();
    },


    onDragStart(element, event, node) {
      this.dragNode = node;
      const width = node.width || 220;
      const half = width / 2;
      
      this.dragStartX = event.x - half;
      this.dragStartY = event.y;
      this.isDragStarting = true;

      d3.select(element).classed('dragging', true);
    },

    onDrag(element, event) {
      if (!this.dragNode) return;

      const state = this.chart.getChartState();
      const g = d3.select(element);

      if (this.isDragStarting) {
        this.isDragStarting = false;
        this.$refs.chartContainer.classList.add('dragging-active');

        g.raise();

        const descendants = this.dragNode.descendants();
        const linksToRemove = [...(descendants || []), this.dragNode];
        const nodesToRemove = descendants.filter(
          (x) => x.data.id !== this.dragNode.data.id
        );

        state.linksWrapper
          .selectAll('path.link')
          .data(linksToRemove, (d) => state.nodeId(d))
          .remove();

        if (nodesToRemove) {
          state.nodesWrapper
            .selectAll('g.node')
            .data(nodesToRemove, (d) => state.nodeId(d))
            .remove();
        }
      }

      this.dropNode = null;
      
      const cP = {
        midX: event.x + this.dragNode.width / 2,
        midY: event.y + this.dragNode.height / 2,
      };

      const allNodes = d3.selectAll('g.node:not(.dragging)');
      allNodes.select('rect').attr('fill', 'none');

      const vm = this;

      allNodes
        .filter(function (d2) {
          const cPInner = {
            left: d2.x,
            right: d2.x + d2.width,
            top: d2.y,
            bottom: d2.y + d2.height,
          };

          if (
            cP.midX > cPInner.left &&
            cP.midX < cPInner.right &&
            cP.midY > cPInner.top &&
            cP.midY < cPInner.bottom &&
            this.classList.contains('droppable')
          ) {
            vm.dropNode = d2;
            return d2;
          }
        })
        .select('rect')
        .attr('fill', '#E0EDFE');

      this.dragStartX += event.dx;
      this.dragStartY += event.dy;
      g.attr('transform', `translate(${this.dragStartX},${this.dragStartY})`);
    },

    onDragEnd(element, event) {
      this.$refs.chartContainer.classList.remove('dragging-active');

      if (!this.dragNode) return;

      d3.select(element).classed('dragging', false);

      if (!this.dropNode || (this.dragNode.parent && this.dragNode.parent.id === this.dropNode.id)) {
        this.chart.render();
        this.dragNode = null;
        this.dropNode = null;
        return;
      }

      d3.select(element).remove();

      const data = this.chart.getChartState().data;
      const node = data.find((x) => x.id === this.dragNode.data.id);
      const oldManagerIdFromNode = node.managerId || node.data?.managerId;
      const oldManagerId = oldManagerIdFromNode === 'virtual-root-org' ? null : oldManagerIdFromNode;
      
      const dropNodeData = data.find((x) => x.id === this.dropNode.id);
      const isDroppingOnRoot = this.dropNode.id === 'virtual-root-org' || 
                               (!dropNodeData?.managerId && !dropNodeData?.data?.managerId);
      
      
      const newManagerId = this.dropNode.id === 'virtual-root-org' ? null : this.dropNode.id;
      const newManagerIdForChart = this.dropNode.id === 'virtual-root-org' ? 'virtual-root-org' : this.dropNode.id;
      
      node.managerId = newManagerIdForChart;
      if (node.data) {
        node.data.managerId = newManagerIdForChart;
      }
      
      const normalizedUser = this.users.find((u) => u.id === this.dragNode.data.id);
      if (normalizedUser) {
        normalizedUser.managerId = newManagerIdForChart;
      }
      
      const originalUser = this.originalUsers.find((u) => u.id === this.dragNode.data.id);
      if (originalUser) {
        originalUser.managerId = newManagerId;
      }

      this.redoActions = [];
      this.undoActions.push({
        id: this.dragNode.data.id,
        managerId: oldManagerId,
      });

      this.dropNode = null;
      this.dragNode = null;
      
      this.chart.data(this.users).render();
    },

    enableDrag() {
      this.dragEnabled = true;
      this.$refs.chartContainer.classList.add('drag-enabled');
      this.chart.render();
    },

    async disableDrag() {
      if (this.undoActions.length > 0) {
        try {
          const changes = this.undoActions.map(action => {
            const user = this.users.find(u => u.id === action.id);
            let managerId = user ? (user.managerId || null) : null;
            if (managerId === 'virtual-root-org') {
              managerId = null;
            }
            return {
              id: action.id,
              managerId: managerId
            };
          });

          const allUsersAfterChanges = this.originalUsers.map(originalUser => {
            const change = changes.find(c => c.id === originalUser.id);
            if (change) {
              return { ...originalUser, managerId: change.managerId };
            }
            return originalUser;
          });
          
          const usersWithNullManager = allUsersAfterChanges.filter(u => !u.managerId);
          if (usersWithNullManager.length > 1) {
            window.getApp.error(`Solo puede haber un usuario raíz (sin manager). Actualmente ${usersWithNullManager.length} usuarios quedarían sin manager. Por favor, asigna un manager a los usuarios adicionales.`);
            return;
          }

          await this.$http.post('api/user/updateManagerIds', {
            changes: changes
          });

          await this.loadUsers();

          window.getApp.success('Cambios guardados correctamente');
        } catch (error) {
          console.error('Error al guardar cambios:', error);
          // El interceptor de axios ya muestra el error automáticamente
          // No limpiar los arrays si hay error, para que el usuario pueda intentar de nuevo
          return;
        }
      }

      this.dragEnabled = false;
      this.$refs.chartContainer.classList.remove('drag-enabled');
      this.undoActions = [];
      this.redoActions = [];
      this.chart
      console.log("🚀 ~ this.chart:", this.chart)
    },

    cancelDrag() {
      if (this.undoActions.length > 0) {
        const data = this.chart.getChartState().data;
        [...this.undoActions].reverse().forEach((action) => {
          const node = data.find((x) => x.id === action.id);
          if (node) {
            // Convertir null del original a nodo virtual si es necesario
            const managerId = action.managerId === null ? 'virtual-root-org' : action.managerId;
            node.managerId = managerId;
            if (node.data) {
              node.data.managerId = managerId;
            }
          }
          // Actualizar también en los datos normalizados
          const normalizedUser = this.users.find((u) => u.id === action.id);
          if (normalizedUser) {
            normalizedUser.managerId = action.managerId === null ? 'virtual-root-org' : action.managerId;
          }
          // Actualizar también en los datos originales
          const originalUser = this.originalUsers.find((u) => u.id === action.id);
          if (originalUser) {
            originalUser.managerId = action.managerId;
          }
        });
        // Actualizar el chart con los datos revertidos
        this.chart.data(this.users).render();
      }
      this.disableDrag();
    },

    undo() {
      const action = this.undoActions.pop();
      if (action) {
        const node = this.chart.getChartState().data.find((x) => x.id === action.id);
        const currentManagerId = node?.managerId || node?.data?.managerId;
        const currentManagerIdForRedo = currentManagerId === 'virtual-root-org' ? null : currentManagerId;
        
        this.redoActions.push({ id: action.id, managerId: currentManagerIdForRedo });
        
        const managerIdToSet = action.managerId === null ? 'virtual-root-org' : action.managerId;
        
        if (node) {
          node.managerId = managerIdToSet;
          if (node.data) {
            node.data.managerId = managerIdToSet;
          }
        }
        
        const normalizedUser = this.users.find((u) => u.id === action.id);
        if (normalizedUser) {
          normalizedUser.managerId = managerIdToSet;
        }
        
        const originalUser = this.originalUsers.find((u) => u.id === action.id);
        if (originalUser) {
          originalUser.managerId = action.managerId;
        }
        
        this.chart.data(this.users).render();
      }
    },

    redo() {
      const action = this.redoActions.pop();
      if (action) {
        const node = this.chart.getChartState().data.find((x) => x.id === action.id);
        const currentManagerId = node?.managerId || node?.data?.managerId;
        const currentManagerIdForUndo = currentManagerId === 'virtual-root-org' ? null : currentManagerId;

        this.undoActions.push({ id: action.id, managerId: currentManagerIdForUndo });
        
        const managerIdToSet = action.managerId === null ? 'virtual-root-org' : action.managerId;
        
        if (node) {
          node.managerId = managerIdToSet;
          if (node.data) {
            node.data.managerId = managerIdToSet;
          }
        }
        
        const normalizedUser = this.users.find((u) => u.id === action.id);
        if (normalizedUser) {
          normalizedUser.managerId = managerIdToSet;
        }
        
        const originalUser = this.originalUsers.find((u) => u.id === action.id);
        if (originalUser) {
          originalUser.managerId = action.managerId;
        }
        
        this.chart.data(this.users).render();
      }
    },

    generateContent(d) {
      console.log(d.data.profilePicSrc);
      const imageDiffVert = 25 + 2;
      return `
        <div class="node-container" style='width:${d.width}px;height:${d.height}px;padding-top:${imageDiffVert - 2}px;padding-left:1px;padding-right:1px'>
          <div class="content-container" style="font-family: 'Inter', sans-serif;  margin-left:-1px;width:${d.width - 2}px;height:${d.height - imageDiffVert}px;border-radius:10px;border: ${d.data._highlighted || d.data._upToTheRootHighlighted ? '5px solid #E27396' : '1px solid #167bd7'}">
            <div style="display:flex;visibility:hidden;justify-content:flex-end;margin-top:5px;margin-right:8px">#</div>
            <div class="v-avatar avatar cursor-pointer grey" style="margin-top:${-imageDiffVert - 20}px;margin-left:20px;height:40px;min-width:40px;width:40px;">
              <div class="v-image v-responsive theme--light">
                <div class="v-responsive__sizer" style="padding-bottom:85.75%;"></div>
                <div class="v-image__image v-image__image--cover" style="background-image:url('${d.data.profilePicSrc || ''}');background-position:center center;"></div>
                <div class="v-responsive__content" style="width:40px;"></div>
              </div>
            </div>
            <div style="font-size:15px;color:#08011E;margin-left:20px;margin-top:10px">  ${d.data.fullName} </div>
            <div style="color:#716E7B;margin-left:20px;margin-top:3px;font-size:10px;"> ${d.data.roles} </div>
          </div>
        </div>
      `;
    }
  }
};
</script>

<style>
.node-container { }
.content-container { background-color: #ffffff; }

.drag-enabled:not(.dragging-active) .node.draggable rect {
  stroke: grey;
  stroke-width: 3px;
  stroke-dasharray: 5px;
  /* stroke: var(--v-primary-base); */
  cursor: grab;
}

.drag-enabled.dragging-active .droppable rect {
  stroke: var(--v-primary-base);
  stroke-width: 3px;
  stroke-dasharray: 5px;
}

.node.dragging rect {
  stroke-dasharray: 0 !important;
  stroke-width: 0 !important;
}

.node.dragging .content-container {
  background-color: #ffffff;
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  cursor: grabbing;
}
</style>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500&display=swap');

.org-chart-wrapper {
  font-family: 'Inter', sans-serif;
  background-color: var(--v-newDesignBackground-base);
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.controls {
  padding: 10px;
  background: var(--v-newDesignBackground-base);;
  border-bottom: 1px solid var(--v-background-lighten2);
  display: flex;
  gap: 10px;
  align-items: center;
  z-index: 10;
}

.chart-container {
  flex-grow: 1;
  width: 100%;
  /* IMPORTANTE: altura explícita o flex-grow */
  position: relative;
  overflow: hidden;
  background-color: var(--v-newDesignBackground-base);
}

.loading-container {
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--v-newDesignBackground-base);;
}

.loading-message {
  font-size: 16px;
  color: #716E7B;
}
::v-deep path.link {
  stroke: var(--v-primary-base); 
  stroke-width: 2px;
  fill: none;
  stroke-opacity: 0.6;
}
/*
::v-deep .svg-chart-container {
  width: 100%;
}
*/

.action-btn {
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid #ccc;
  cursor: pointer;
  background: var(--v-newDesignBackground-base);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-btn.primary {
  background-color: var(--v-primary-base);
  color: white;
  border: none;
}
.action-btn.warning {
  background-color: var(--v-error-base) !important;
  color: white;
  border: none;
}

.drag-actions {
  display: flex;
  gap: 10px;
}
</style>